// WHICH firmware a device will fetch, WHEN that build was made, and whether the
// device is already running it.
//
// firmwareUpdate.js answers "will this device receive the command". This answers
// "is there any point sending it".
//
// ── The app does not choose the file, and cannot ───────────────────────────
// Update Code publishes the word `updatecode` and nothing else. The DEVICE then
// asks the server for its own URL — OtaForSwitching.ino:2081 posts to
// `GetDeviceOTAUrlLink` and flashes whatever comes back. So everything here is
// REPORTING: getting it wrong shows the operator the wrong filename, it cannot
// send the wrong firmware to a device.
//
// ── Where the filename comes from ──────────────────────────────────────────
//     DevicesInstalled.OTABinFileId -> OTABinFile.BinFileName
//     -> http://smartifyindia.co.in/uploads/bin/<BinFileName>
//
// ⚠️ THE DEVICE'S OWN OTABinFileId OVERRIDES ITS TYPE'S, and they really do
// differ: 935809 is DeviceID 49 (4 Touch), whose TYPE points at
// OtaForSwitchingNewDesign.ino.bin, but the device carries OTABinFileId 9 and
// flashes OtaForTuya.ino.bin. Resolving by device TYPE would name the wrong file
// for exactly the devices somebody deliberately special-cased.
//
// GetDeviceDetailbyUniqueNumber returns the device-level join, so BinFileName on
// that row is the right one. Checked against `GetDeviceOTAUrlLink` — the call
// the firmware itself makes — for all 43 devices at location 1: 43 matched, 0
// differed. One call per device therefore answers everything, instead of two.
//
// ⚠️ DeviceType.OTAUpdateLink IS NOT THIS. It is a legacy column, it is not what
// GetDeviceOTAUrlLink reads, and for most types it holds a lowercased URL
// (`otaforswitchingnewdesign.ino.bin`) that 404s — nginx is case-sensitive and
// the file is `OtaForSwitchingNewDesign.ino.bin`. Nothing breaks because nothing
// reads it, but do not "fix" the resolution to use it.
//
// ⚠️ THE SAME TRAP ON THE OTABinFile ROW, and here it is measurable. Read
// straight off the table, `NewOTAUpdateLinkNuvo` DISAGREES WITH `BinFileName`
// on at least four of the fifteen rows:
//
//   id  BinFileName                    NewOTAUpdateLinkNuvo points at
//    9  OtaForTuya.ino.bin             OtaForSwitchingNewDesign.ino.bin
//   11  OTA_CURRENTSENSOR.ino.bin      OtaForSwitchingNewDesign.ino.bin
//   12  OTAShutterController.ino.bin   OtaForSwitchingNewDesign.ino.bin
//
// `BinFileName`, `HexFileUrl` and `HexFileDateTime` agree with each other on
// every row; the *UpdateLink* columns do not agree with any of them. Resolving
// the filename from a link column would therefore report three different device
// families as all running the switching firmware. Use BinFileName + the
// HexFile* pair, which is what this module does.
//
// `NewOtaUpdateDateTime` is likewise stale — id 1 says 2023-10-01 against a
// HexFileDateTime of 2026-08-02 — and `NuvoUpdateDateTime` is the 1901 sentinel
// on every single row.
//
// ── Why the build date is the DATABASE's, not the file's ───────────────────
// The true build time is the .bin's HTTP Last-Modified. The app cannot read it:
// the file host (nginx on :80) sends NO Access-Control-Allow-Origin, so the
// browser blocks the response headers. The API host does send CORS; the file
// host does not.
//
// So OTABinFile.HexFileDateTime is used instead. Measured against the real file
// for OtaForSwitching.ino.bin: the row says 12:19:46 (IST-as-UTC = 06:49:46 real)
// against a file written 06:44:41 — the upload tool stamps the row about five
// minutes after writing the file. Immaterial for "was this device flashed since
// the build", which is a question about days.
//
// (A native build could read Last-Modified — Capacitor's HTTP is not
// CORS-bound — but then mobile and desktop would answer differently for the same
// device. One source, same answer everywhere.)

import { getDeviceDetail } from '../api/api';
import { SERVER_TZ_OFFSET_MS } from './deviceStatus';

/** Where the bin files live. Only used to show the full URL. */
export const BIN_BASE_URL = 'http://smartifyindia.co.in/uploads/bin/';

export const CURRENT = 'current';
export const STALE = 'stale';
export const UNKNOWN_STATE = 'unknown';

/**
 * How far a flash may PRECEDE its build stamp and still count as that build.
 *
 * ⚠️ WITHOUT THIS, DEVICES THAT JUST UPDATED REPORT "NEEDS UPDATE". The two
 * timestamps do not come from the same event and cannot be compared exactly:
 *
 *   CodeUpdated      written by the DEVICE, when it starts flashing
 *   HexFileDateTime  written by the UPLOAD TOOL, after it writes the .bin
 *
 * Measured on OtaForSwitching.ino.bin: the file was written at 06:44:41 UTC and
 * the row stamped 06:49:46 — five minutes later. So a device that flashed that
 * exact build at 12:18 is compared against a build that claims 12:19:46 and
 * comes out "older", which is how three up-to-date devices ended up ticked for
 * a pointless reflash.
 *
 * Five minutes covers it with room to spare, and the number is not a guess. The
 * gap between flash and build stamp, measured across all 43 devices at location
 * 1, falls in two clearly separated groups:
 *
 *   0.7, 0.7, 1.6, 1.6 min     the artefact — devices that ARE on this build
 *   ── nothing at all here ──
 *   59 min, 3 days, 7 days,    genuinely behind
 *   69 days, 100 days, 137 days
 *
 * So the threshold sits in an empty band, three times above the largest
 * artefact and an order of magnitude below the smallest real one. Nothing
 * marginal is near it, in either direction.
 *
 * The cost is narrow and worth stating: a build published within five minutes
 * of a device flashing the PREVIOUS one reads as current. That is a dev-loop
 * situation — build, upload, flash, build again — and the row can still be
 * ticked by hand.
 */
export const BUILD_STAMP_TOLERANCE_MS = 5 * 60 * 1000;

// The server writes IST but labels it UTC — the same fault deviceStatus.js
// corrects for heartbeats, and the same constant, so fixing the backend fixes
// both at once.
//
// Returns real epoch ms, or null. The 1901 sentinel the DB uses for "never" is
// treated as absent rather than as a date in 1901.
export function parseServerTime(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (s.startsWith('1901') || s.startsWith('0000')) return null;
  const t = Date.parse(s.replace(' ', 'T'));
  if (Number.isNaN(t)) return null;
  return t - SERVER_TZ_OFFSET_MS;
}

/**
 * Firmware facts for one device, from a DevicesInstalled row.
 *
 * Pure — takes the row, does no I/O — so the comparison is testable without a
 * server.
 *
 * -> { binFile, binUrl, builtAt, flashedAt, state }
 *
 * `state` is deliberately three-valued. A device with no recorded flash, or a
 * build with no date, is UNKNOWN — not stale. Guessing "stale" would push the
 * operator into reflashing a fleet on no evidence, which is the expensive
 * mistake; guessing "current" would hide devices that genuinely need it.
 */
export function firmwareStateOf(row) {
  const binFile = String(row?.BinFileName || '').trim();
  const builtAt = parseServerTime(row?.HexFileDateTime);
  const flashedAt = parseServerTime(row?.CodeUpdated);
  let state = UNKNOWN_STATE;
  // True when the flash is EARLIER than the build stamp but inside the
  // tolerance — i.e. counted as current only because of the gap between the two
  // clocks. The UI says so, rather than leaving "Up to date" next to a date that
  // plainly reads two minutes older.
  let withinTolerance = false;
  if (builtAt != null && flashedAt != null) {
    state = flashedAt >= builtAt - BUILD_STAMP_TOLERANCE_MS ? CURRENT : STALE;
    withinTolerance = state === CURRENT && flashedAt < builtAt;
  }
  return {
    binFile,
    binUrl: binFile ? BIN_BASE_URL + binFile : '',
    builtAt,
    flashedAt,
    state,
    withinTolerance,
  };
}

// One request per device, so the same ceiling firmwareUpdate uses applies here.
// A browser allows about six connections per host; firing a location's worth at
// once puts the ones at the back behind their own timeout, which is what made
// whole locations read as dead before.
export const INFO_CONCURRENCY = 6;

async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      out[i] = await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => worker()));
  return out;
}

const unoOf = (d) => String((d && (d.UniqueNumber ?? d.uniqueNumber)) ?? '');

/**
 * Firmware facts for a list of devices.
 *
 * -> Map keyed by unique number. A device whose detail call fails is simply
 *    absent, and callers treat absent as UNKNOWN — a lookup failure must never
 *    read as "up to date" and quietly exclude a device from the rollout.
 */
export async function firmwareInfoFor(devices) {
  const list = (devices || []).filter((d) => unoOf(d));
  const rows = await mapLimit(list, INFO_CONCURRENCY, (d) => getDeviceDetail(unoOf(d))
    .then((res) => (Array.isArray(res.data) ? res.data[0] : null))
    .catch(() => null));
  const out = new Map();
  list.forEach((d, i) => {
    if (rows[i]) out.set(unoOf(d), firmwareStateOf(rows[i]));
  });
  return out;
}

/**
 * Split a live list into the ones that need the update and the ones already on
 * it.
 *
 * ⚠️ UNKNOWN GOES WITH "NEEDS IT". `CodeUpdated` is stamped by AddCodeUpdated
 * BEFORE ESPhttpUpdate runs (OtaForSwitching.ino:2097), so it records that an
 * update was ATTEMPTED, not that it succeeded. A device whose flash failed
 * therefore looks current — and that is precisely the device most worth
 * retrying. Erring towards sending costs a redundant reflash; erring the other
 * way leaves broken firmware in place and reports success.
 */
export function splitByFirmware(devices, info) {
  const needs = [];
  const current = [];
  (devices || []).forEach((d) => {
    const f = info && info.get(unoOf(d));
    (f && f.state === CURRENT ? current : needs).push(d);
  });
  return { needs, current };
}
